import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrash, faBan } from '@fortawesome/free-solid-svg-icons';
import {
  createNewInventoryUnit,
  getProductAvailabilityAndInUse,
  findStockItemRowIndex,
  isStockRowDeployed,
  stockDeployedToStatus,
  stockRowQuantity,
  type InventoryUnit,
  type InventoryUnitStatus,
  type InventoryUnitCondition,
  type ProductType,
  type ProductCatalogStatus,
} from '../data/inventory';
import { useInventory } from '../context/InventoryContext';
import { composeAssetTagSerial, extractSerialNumberPortion, findUnitBySerialNumber } from '../utils/assetTagBarcode';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import { SortableTh, StaticTh } from '../components/SortableTh';
import PartsInventoryTable from '../components/PartsInventoryTable';
import type { PartsRowStatus } from '../data/timEInventoryList';
import { useTableSortState } from '../hooks/useTableSort';
import { sortRows } from '../utils/tableSort';
import './Page.css';
import './ClientDetail.css';
import './ProductDetail.css';

/** Format an ISO date (YYYY-MM-DD) as M/D/YYYY without timezone drift; pass through anything else. */
function formatInstallationDate(raw?: string): string {
  const s = String(raw ?? '').trim();
  if (!s) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) {
    const [, y, mo, d] = m;
    return `${Number(mo)}/${Number(d)}/${y}`;
  }
  return s;
}

const ProductDetail: React.FC = () => {
  const navigate = useNavigate();
  const { productId } = useParams<{ productId: string }>();
  const [searchParams] = useSearchParams();
  const {
    products,
    units: allUnits,
    setProducts,
    setUnits,
    timEParts,
    bimEParts,
    setTimEParts,
    setBimEParts,
    loaded,
  } = useInventory();
  const product = productId ? products.find((p) => p.id === productId) : null;
  const units = useMemo(
    () => (productId ? allUnits.filter((u) => u.productId === productId) : []),
    [productId, allUnits]
  );
  const [addUnitOpen, setAddUnitOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    inventory: false,
  });
  const toggleSection = (key: string) =>
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  const [editingUnit, setEditingUnit] = useState<InventoryUnit | null>(null);
  const [countryOfOrigin, setCountryOfOrigin] = useState('');
  const [productType, setProductType] = useState('');
  const [model, setModel] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [status, setStatus] = useState<InventoryUnitStatus | ''>('');
  const [condition, setCondition] = useState<InventoryUnitCondition | ''>('');
  const [manufacturer, setManufacturer] = useState('');
  const [location, setLocation] = useState('');
  const [business, setBusiness] = useState('');
  const [installationDate, setInstallationDate] = useState('');
  const [trashBins, setTrashBins] = useState<string>('');
  const [linenBins, setLinenBins] = useState<string>('');
  const [rollingBases, setRollingBases] = useState<string>('');
  const [staticBases, setStaticBases] = useState<string>('');
  const [stockQuantity, setStockQuantity] = useState('1');
  const [unitAssembled, setUnitAssembled] = useState(true);
  /** Stock rows only: Yes = the items sit at a business, No = office/warehouse. */
  const [unitDeployed, setUnitDeployed] = useState(false);

  const isFullInventoryProduct = !!product && (product.name === 'TIM-E Bot' || product.name === 'BIM-E bot');
  const isStockProduct = !!product && product.type === 'Stock Item';

  /**
   * Parts inventory belongs to the bot it builds, so each bot's product page owns its list.
   * The dashboard low-stock card links here with ?part=<name> to point at a single row.
   */
  const partsSource: 'tim-e' | 'bim-e' | null =
    product?.name === 'TIM-E Bot' ? 'tim-e' : product?.name === 'BIM-E bot' ? 'bim-e' : null;
  const highlightPart = searchParams.get('part') ?? undefined;

  const handleSavePartsRow = (
    index: number,
    patch: { available: string; lowStockBuffer: number | null }
  ) => {
    if (partsSource === 'tim-e') {
      setTimEParts(timEParts.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    } else if (partsSource === 'bim-e') {
      setBimEParts(bimEParts.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    }
  };

  const handleSetPartsRowStatus = (index: number, nextStatus: PartsRowStatus) => {
    if (partsSource === 'tim-e') {
      setTimEParts(timEParts.map((row, i) => (i === index ? { ...row, status: nextStatus } : row)));
    } else if (partsSource === 'bim-e') {
      setBimEParts(bimEParts.map((row, i) => (i === index ? { ...row, status: nextStatus } : row)));
    }
  };

  /** Free-text search across every field tied to a unit. */
  const [unitSearchQuery, setUnitSearchQuery] = useState('');
  const unitSearchLower = unitSearchQuery.trim().toLowerCase();
  const filteredUnits = useMemo(() => {
    if (!unitSearchLower) return units;
    return units.filter((u) => {
      const fields = [
        u.location,
        u.business,
        u.productType,
        u.serialNumber,
        u.tempSerialNumber,
        u.status,
        u.condition,
        u.model,
        u.manufacturer,
        u.countryOfOrigin,
        u.installationDate,
        u.assembled ? 'Yes' : 'No',
        u.stockQuantity != null ? String(u.stockQuantity) : '',
        isStockRowDeployed(u) ? 'Yes' : 'No',
      ];
      return fields.some((v) => String(v ?? '').toLowerCase().includes(unitSearchLower));
    });
  }, [units, unitSearchLower]);

  const unitSort = useTableSortState();
  const sortedUnits = useMemo(
    () =>
      sortRows(filteredUnits, unitSort.sortKey, unitSort.sortDir, (u, key) => {
        switch (key) {
          case 'city':
            return u.location || '';
          case 'business':
            return u.business || '';
          case 'productType':
            return u.productType || '';
          case 'serial':
            return u.serialNumber || '';
          case 'status':
            return u.status || '';
          case 'condition':
            return u.condition || '';
          case 'manufacturer':
            return u.manufacturer || '';
          case 'count':
            return u.stockQuantity ?? 1;
          case 'deployed':
            return isStockRowDeployed(u) ? 1 : 0;
          case 'assembled':
            return u.assembled ? 1 : 0;
          case 'installationDate':
            return u.installationDate || '';
          default:
            return '';
        }
      }),
    [filteredUnits, unitSort.sortKey, unitSort.sortDir]
  );

  // Edit product details modal
  const [editProductOpen, setEditProductOpen] = useState(false);
  const [editProductName, setEditProductName] = useState('');
  const [editProductType, setEditProductType] = useState<ProductType>('Robot');
  const [editProductSku, setEditProductSku] = useState('');
  const [editProductReplacementCost, setEditProductReplacementCost] = useState('');
  const [editProductLowStockBuffer, setEditProductLowStockBuffer] = useState('');
  const [editCatalogStatus, setEditCatalogStatus] = useState<ProductCatalogStatus>('Active');

  const openEditProductModal = () => {
    if (!product) return;
    setEditProductName(product.name);
    setEditProductType(product.type);
    setEditProductSku(product.sku || '');
    setEditProductReplacementCost((product.replacementCostPerItem ?? '').trim());
    setEditProductLowStockBuffer(product.lowStockBuffer != null ? String(product.lowStockBuffer) : '');
    setEditCatalogStatus(product.catalogStatus === 'Void' ? 'Void' : 'Active');
    setEditProductOpen(true);
  };

  const lockProductDetailFields = editCatalogStatus === 'Void';

  const handleSaveProduct = (e: React.FormEvent) => {
    e.preventDefault();
    if (!product) return;
    const bufRaw = editProductLowStockBuffer.trim();
    const lowStockBuffer = bufRaw === '' ? undefined : Math.max(0, Math.floor(Number(bufRaw))) || undefined;
    const wasVoid = product.catalogStatus === 'Void';
    const nextVoid = editCatalogStatus === 'Void';

    if (wasVoid && nextVoid) {
      setEditProductOpen(false);
      return;
    }

    if (wasVoid && !nextVoid) {
      const updated = products.map((p) =>
        p.id === product.id
          ? {
              ...p,
              catalogStatus: 'Active' as const,
              name: editProductName.trim(),
              type: editProductType,
              sku: editProductSku.trim(),
              replacementCostPerItem: editProductReplacementCost.trim(),
              lowStockBuffer: lowStockBuffer ?? null,
            }
          : p
      );
      setProducts(updated);
      setEditProductOpen(false);
      return;
    }

    if (!wasVoid && nextVoid) {
      setProducts(products.map((p) => (p.id === product.id ? { ...p, catalogStatus: 'Void' as const } : p)));
      setEditProductOpen(false);
      return;
    }

    const updated = products.map((p) =>
      p.id === product.id
        ? {
            ...p,
            name: editProductName.trim(),
            type: editProductType,
            sku: editProductSku.trim(),
            replacementCostPerItem: editProductReplacementCost.trim(),
            lowStockBuffer: lowStockBuffer ?? null,
          }
        : p
    );
    setProducts(updated);
    setEditProductOpen(false);
  };

  const [voidProductOpen, setVoidProductOpen] = useState(false);

  const handleConfirmVoidProduct = () => {
    if (!product) return;
    setProducts(products.map((p) => (p.id === product.id ? { ...p, catalogStatus: 'Void' as const } : p)));
    setVoidProductOpen(false);
  };

  const { availability: availableCount, inUse: inUseCount, unavailable: unavailableCount } = useMemo(
    () =>
      productId && product
        ? getProductAvailabilityAndInUse(productId, allUnits, product.type)
        : { availability: 0, inUse: 0, unavailable: 0 },
    [productId, allUnits, product?.type]
  );

  const stockTotalQuantity = useMemo(
    () => units.reduce((sum, u) => sum + stockRowQuantity(u), 0),
    [units]
  );

  const openAddModal = () => {
    if (product?.catalogStatus === 'Void') return;
    setEditingUnit(null);
    setCountryOfOrigin('');
    setProductType('');
    setModel('');
    setSerialNumber('');
    setStatus('');
    setCondition('');
    setManufacturer('');
    setLocation('');
    setBusiness('');
    setInstallationDate('');
    setTrashBins('');
    setLinenBins('');
    setRollingBases('');
    setStaticBases('');
    setStockQuantity('1');
    setUnitAssembled(true);
    setUnitDeployed(false);
    setAddUnitOpen(true);
  };

  const openEditModal = (u: InventoryUnit) => {
    setEditingUnit(u);
    setCountryOfOrigin(u.countryOfOrigin || '');
    setProductType(u.productType || '');
    setModel(u.model || '');
    setSerialNumber(extractSerialNumberPortion(u.serialNumber, u.countryOfOrigin, u.productType));
    setStatus(u.status || '');
    setCondition(u.condition || '');
    setManufacturer(u.manufacturer || '');
    setLocation(u.location || '');
    setBusiness(u.business || '');
    setInstallationDate(u.installationDate || '');
    setTrashBins(u.trashBins !== undefined ? String(u.trashBins) : '');
    setLinenBins(u.linenBins !== undefined ? String(u.linenBins) : '');
    setRollingBases(u.rollingBases !== undefined ? String(u.rollingBases) : '');
    setStaticBases(u.staticBases !== undefined ? String(u.staticBases) : '');
    setStockQuantity(String(u.stockQuantity != null && u.stockQuantity > 0 ? u.stockQuantity : 1));
    setUnitAssembled(u.assembled === true);
    setUnitDeployed(isStockRowDeployed(u));
    setAddUnitOpen(true);
  };

  const resetUnitFormAndClose = () => {
    setEditingUnit(null);
    setCountryOfOrigin('');
    setProductType('');
    setModel('');
    setSerialNumber('');
    setStatus('');
    setCondition('');
    setManufacturer('');
    setLocation('');
    setBusiness('');
    setInstallationDate('');
    setTrashBins('');
    setLinenBins('');
    setRollingBases('');
    setStaticBases('');
    setStockQuantity('1');
    setUnitAssembled(true);
    setUnitDeployed(false);
    setAddUnitOpen(false);
  };

  const handleSaveUnit = (e: React.FormEvent) => {
    e.preventDefault();
    if (product?.catalogStatus === 'Void') return;
    if (isStockProduct && productId) {
      const qty = Math.max(1, parseInt(stockQuantity, 10) || 1);
      const mfr = manufacturer.trim();
      const loc = location.trim();
      if (editingUnit) {
        setUnits(
          allUnits.map((u) =>
            u.id === editingUnit.id
              ? {
                  ...u,
                  manufacturer: mfr || undefined,
                  location: loc || undefined,
                  stockQuantity: qty,
                  assembled: unitAssembled,
                  countryOfOrigin: '',
                  model: '',
                  serialNumber: '',
                  productType: undefined,
                  business: undefined,
                  installationDate: undefined,
                  status: stockDeployedToStatus(unitDeployed),
                  condition: undefined,
                }
              : u
          )
        );
      } else {
        const idx = findStockItemRowIndex(allUnits, productId, mfr, loc, unitDeployed);
        if (idx >= 0) {
          const prev = allUnits[idx]!;
          setUnits(
            allUnits.map((x, i) =>
              i === idx ? { ...x, stockQuantity: (prev.stockQuantity ?? 1) + qty, assembled: unitAssembled } : x
            )
          );
        } else {
          const newUnit = createNewInventoryUnit(productId, '', '', '', {
            manufacturer: mfr || undefined,
            location: loc || undefined,
            stockQuantity: qty,
            status: stockDeployedToStatus(unitDeployed),
            assembled: unitAssembled,
          });
          setUnits([...allUnits, newUnit]);
        }
      }
      resetUnitFormAndClose();
      return;
    }

    const effectiveSerial = serialNumber.trim();
    const statusVal = status || undefined;
    const conditionVal = condition || undefined;
    const manufacturerVal = manufacturer.trim() || undefined;
    const modelVal = model.trim() || '';
    const locationVal = location.trim() || undefined;
    const businessVal = business.trim() || undefined;
    const installationDateVal = installationDate.trim() || undefined;
    const trashBinsVal = trashBins === '' ? undefined : parseInt(trashBins, 10);
    const linenBinsVal = linenBins === '' ? undefined : parseInt(linenBins, 10);
    const rollingBasesVal = rollingBases === '' ? undefined : parseInt(rollingBases, 10);
    const staticBasesVal = staticBases === '' ? undefined : parseInt(staticBases, 10);
    /** Block duplicate serials (ignore the unit being edited and empty serials). */
    const composedSerial = composeAssetTagSerial(countryOfOrigin, productType, effectiveSerial);
    const dupUnit = findUnitBySerialNumber(allUnits, composedSerial);
    if (dupUnit && (!editingUnit || dupUnit.id !== editingUnit.id)) {
      const dupProduct = products.find((p) => p.id === dupUnit.productId);
      alert(
        `Serial number "${composedSerial}" is already in inventory` +
          `${dupProduct ? ` under ${dupProduct.name}` : ''}. ` +
          'Use a different serial number to avoid a duplicate.'
      );
      return;
    }
    if (editingUnit) {
      setUnits(
        allUnits.map((u) =>
          u.id === editingUnit.id
            ? {
                ...u,
                countryOfOrigin: countryOfOrigin.trim(),
                productType: productType.trim() || undefined,
                model: modelVal,
                serialNumber: composeAssetTagSerial(countryOfOrigin, productType, effectiveSerial),
                status: statusVal as InventoryUnitStatus | undefined,
                condition: conditionVal as InventoryUnitCondition | undefined,
                manufacturer: manufacturerVal,
                location: locationVal,
                business: businessVal,
                installationDate: installationDateVal,
                trashBins: trashBinsVal,
                linenBins: linenBinsVal,
                rollingBases: rollingBasesVal,
                staticBases: staticBasesVal,
                assembled: unitAssembled,
              }
            : u
        )
      );
    } else if (productId) {
      const newUnit = createNewInventoryUnit(productId, countryOfOrigin.trim(), modelVal, effectiveSerial, {
        productType: productType.trim() || undefined,
        status: statusVal as InventoryUnitStatus | undefined,
        condition: conditionVal as InventoryUnitCondition | undefined,
        manufacturer: manufacturerVal,
        location: locationVal,
        business: businessVal,
        installationDate: installationDateVal,
        trashBins: trashBinsVal,
        linenBins: linenBinsVal,
        rollingBases: rollingBasesVal,
        staticBases: staticBasesVal,
        assembled: unitAssembled,
      });
      setUnits([...allUnits, newUnit]);
    }
    resetUnitFormAndClose();
  };

  const [unitToDelete, setUnitToDelete] = useState<InventoryUnit | null>(null);

  useEffect(() => {
    const anyOpen = addUnitOpen || editProductOpen || voidProductOpen;
    document.body.style.overflow = anyOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [addUnitOpen, editProductOpen, voidProductOpen]);

  const handleRequestDeleteUnit = (e: React.MouseEvent, u: InventoryUnit) => {
    e.stopPropagation();
    setUnitToDelete(u);
  };

  const handleConfirmDeleteUnit = () => {
    if (!unitToDelete || product?.catalogStatus === 'Void') return;
    setUnits(allUnits.filter((u) => u.id !== unitToDelete.id));
    setUnitToDelete(null);
    // If the edit modal was open for this unit, close it too
    if (editingUnit?.id === unitToDelete.id) {
      setAddUnitOpen(false);
      setEditingUnit(null);
    }
  };

  if (!productId || !product) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <div className="page-content">
              <p className="page-subtitle">Product not found.</p>
              <button type="button" className="back-button" onClick={() => navigate('/inventory')}>
                Back to Inventory
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const displayName = product.name;
  const isCatalogVoid = product.catalogStatus === 'Void';
  const unitsLocked = isCatalogVoid;

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <PageHeader
            title={displayName}
            subtitle={`${product.type} — Product inventory`}
            onBack={() => navigate('/inventory')}
            backLabel="Back"
          />
          <div className="page-content">
            {/* Product info card */}
            <div className={`product-info-card${isCatalogVoid ? ' product-info-card--void' : ''}`}>
              <div className="product-info-header">
                <h3 className="section-title">Product Details</h3>
                <div className="client-action-buttons">
                  <button
                    type="button"
                    className="client-action-btn client-action-btn--edit"
                    data-tooltip="Edit product"
                    onClick={openEditProductModal}
                    aria-label="Edit product"
                  >
                    <FontAwesomeIcon icon={faPenToSquare} />
                  </button>
                  {!isCatalogVoid && (
                    <button
                      type="button"
                      className="client-action-btn client-action-btn--delete"
                      data-tooltip="Void product"
                      onClick={() => setVoidProductOpen(true)}
                      aria-label="Void product"
                    >
                      <FontAwesomeIcon icon={faBan} />
                    </button>
                  )}
                </div>
              </div>
              <div className="product-info-grid">
                <div className="product-info-item">
                  <label className="product-info-label">Product Name</label>
                  <p className="product-info-value">{product.name}</p>
                </div>
                <div className="product-info-item">
                  <label className="product-info-label">Type</label>
                  <p className="product-info-value">{product.type}</p>
                </div>
                <div className="product-info-item">
                  <label className="product-info-label">SKU</label>
                  <p className="product-info-value">{product.sku || '—'}</p>
                </div>
                <div className="product-info-item">
                  <label className="product-info-label">Replacement cost (per item)</label>
                  <p className="product-info-value">{product.replacementCostPerItem?.trim() ? product.replacementCostPerItem : '—'}</p>
                </div>
                <div className="product-info-item">
                  <label className="product-info-label">Catalog status</label>
                  <p className={`product-info-value${isCatalogVoid ? ' product-catalog-status-void' : ''}`}>
                    {isCatalogVoid ? 'Void' : 'Active'}
                  </p>
                </div>
              </div>

              <hr className="client-info-divider" />

              <h4 className="client-info-subsection-title">Unit Counts</h4>
              <div className="product-info-grid">
                {isStockProduct ? (
                  <>
                    <div className="product-info-item">
                      <label className="product-info-label">Placement rows</label>
                      <p className="product-info-value">{units.length}</p>
                    </div>
                    <div className="product-info-item">
                      <label className="product-info-label">Total quantity</label>
                      <p className="product-info-value">{stockTotalQuantity}</p>
                    </div>
                    <div className="product-info-item">
                      <label className="product-info-label">Deployed (at a business)</label>
                      <p className="product-info-value">{inUseCount}</p>
                    </div>
                    <div className="product-info-item">
                      <label className="product-info-label">Available (for low-stock)</label>
                      <p className="product-info-value">{availableCount}</p>
                    </div>
                    <div className="product-info-item">
                      <label className="product-info-label">Low stock buffer</label>
                      <p className="product-info-value">{product.lowStockBuffer != null && product.lowStockBuffer > 0 ? product.lowStockBuffer : '—'}</p>
                    </div>
                    <div className="product-info-item">
                      <label className="product-info-label">Below buffer by</label>
                      <p className="product-info-value">
                        {product.lowStockBuffer != null && product.lowStockBuffer > 0 && product.lowStockBuffer > availableCount
                          ? product.lowStockBuffer - availableCount
                          : '—'}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="product-info-item">
                      <label className="product-info-label">Total in Inventory</label>
                      <p className="product-info-value">{availableCount + inUseCount + unavailableCount}</p>
                    </div>
                    <div className="product-info-item">
                      <label className="product-info-label">In Use</label>
                      <p className="product-info-value">{inUseCount}</p>
                    </div>
                    <div className="product-info-item">
                      <label className="product-info-label">Available</label>
                      <p className="product-info-value">{availableCount}</p>
                    </div>
                    <div className="product-info-item">
                      <label className="product-info-label">Unavailable</label>
                      <p className="product-info-value">{unavailableCount}</p>
                    </div>
                    <div className="product-info-item">
                      <label className="product-info-label">Low stock buffer</label>
                      <p className="product-info-value">{product.lowStockBuffer != null && product.lowStockBuffer > 0 ? product.lowStockBuffer : '—'}</p>
                    </div>
                    <div className="product-info-item">
                      <label className="product-info-label">Items need to be produced</label>
                      <p className="product-info-value">
                        {product.lowStockBuffer != null && product.lowStockBuffer > 0 && product.lowStockBuffer > availableCount
                          ? product.lowStockBuffer - availableCount
                          : '—'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="product-table-section">
              <div className="collapsible-header">
                <div className="collapsible-header-left" onClick={() => toggleSection('inventory')}>
                  <h3 className="product-table-title">Inventory For This Product</h3>
                  <span className={`collapse-arrow${!collapsedSections.inventory ? ' collapse-arrow--open' : ''}`}>▶</span>
                </div>
                <button
                  type="button"
                  className="product-add-btn"
                  onClick={openAddModal}
                  disabled={isCatalogVoid}
                  title={isCatalogVoid ? 'Void products cannot have inventory added or edited here.' : undefined}
                >
                  + Add Inventory
                </button>
              </div>
              {!collapsedSections.inventory && (units.length === 0 ? (
                <p className="table-empty">No inventory units yet.</p>
              ) : (
                <>
                  <input
                    type="text"
                    className="page-toolbar-search product-unit-search"
                    placeholder="Search items by any field..."
                    value={unitSearchQuery}
                    onChange={(e) => setUnitSearchQuery(e.target.value)}
                    aria-label="Search items"
                  />
                  {sortedUnits.length === 0 ? (
                    <p className="table-empty">No items match your search.</p>
                  ) : (
                  <div className="product-table-wrapper">
                  <table className="product-table">
                    <thead>
                      <tr>
                        {isStockProduct ? (
                          <>
                            <SortableTh label="Manufacturer" columnKey="manufacturer" sortKey={unitSort.sortKey} sortDir={unitSort.sortDir} onSort={unitSort.toggleSort} />
                            <SortableTh label="Location" columnKey="city" sortKey={unitSort.sortKey} sortDir={unitSort.sortDir} onSort={unitSort.toggleSort} />
                            <SortableTh label="Count" columnKey="count" sortKey={unitSort.sortKey} sortDir={unitSort.sortDir} onSort={unitSort.toggleSort} />
                            <SortableTh label="Deployed" columnKey="deployed" sortKey={unitSort.sortKey} sortDir={unitSort.sortDir} onSort={unitSort.toggleSort} />
                            <SortableTh label="Assembled" columnKey="assembled" sortKey={unitSort.sortKey} sortDir={unitSort.sortDir} onSort={unitSort.toggleSort} />
                          </>
                        ) : (
                          <>
                            <SortableTh label="City" columnKey="city" sortKey={unitSort.sortKey} sortDir={unitSort.sortDir} onSort={unitSort.toggleSort} />
                            <SortableTh label="Business" columnKey="business" sortKey={unitSort.sortKey} sortDir={unitSort.sortDir} onSort={unitSort.toggleSort} />
                            <SortableTh label="Product type" columnKey="productType" sortKey={unitSort.sortKey} sortDir={unitSort.sortDir} onSort={unitSort.toggleSort} />
                            <SortableTh label="Serial number" columnKey="serial" sortKey={unitSort.sortKey} sortDir={unitSort.sortDir} onSort={unitSort.toggleSort} />
                            {product?.type === 'Accessory' ? (
                              <SortableTh label="Count" columnKey="count" sortKey={unitSort.sortKey} sortDir={unitSort.sortDir} onSort={unitSort.toggleSort} />
                            ) : null}
                            <SortableTh label="Status" columnKey="status" sortKey={unitSort.sortKey} sortDir={unitSort.sortDir} onSort={unitSort.toggleSort} />
                            <SortableTh label="Condition" columnKey="condition" sortKey={unitSort.sortKey} sortDir={unitSort.sortDir} onSort={unitSort.toggleSort} />
                            <SortableTh label="Assembled" columnKey="assembled" sortKey={unitSort.sortKey} sortDir={unitSort.sortDir} onSort={unitSort.toggleSort} />
                            <SortableTh label="Installation date" columnKey="installationDate" sortKey={unitSort.sortKey} sortDir={unitSort.sortDir} onSort={unitSort.toggleSort} />
                          </>
                        )}
                        <StaticTh className="product-table-actions-header">Actions</StaticTh>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedUnits.map((u) => (
                        <tr
                          key={u.id}
                          className={unitsLocked ? undefined : 'product-inventory-row-clickable'}
                          onClick={unitsLocked ? undefined : () => openEditModal(u)}
                          style={{ cursor: unitsLocked ? 'default' : 'pointer' }}
                        >
                          {isStockProduct ? (
                            <>
                              <td>{u.manufacturer || '—'}</td>
                              <td>{u.location || '—'}</td>
                              <td>{stockRowQuantity(u)}</td>
                              <td>{isStockRowDeployed(u) ? 'Yes' : 'No'}</td>
                              <td>{u.assembled ? 'Yes' : 'No'}</td>
                            </>
                          ) : (
                            <>
                              <td>{u.location || '—'}</td>
                              <td>{u.business || '—'}</td>
                              <td>{u.productType || '—'}</td>
                              <td>{u.serialNumber || '—'}</td>
                              {product?.type === 'Accessory' ? <td>{stockRowQuantity(u)}</td> : null}
                              <td>{u.status || '—'}</td>
                              <td>{u.condition || '—'}</td>
                              <td>{u.assembled ? 'Yes' : 'No'}</td>
                              <td>{formatInstallationDate(u.installationDate)}</td>
                            </>
                          )}
                          <td className="product-table-actions-cell" onClick={(e) => e.stopPropagation()}>
                            <div className="product-row-actions">
                              {!unitsLocked ? (
                                <>
                                  <button
                                    type="button"
                                    className="product-row-btn product-row-btn--edit"
                                    data-tooltip="Edit"
                                    onClick={() => openEditModal(u)}
                                    aria-label="Edit"
                                  >
                                    <FontAwesomeIcon icon={faPenToSquare} />
                                  </button>
                                  <button
                                    type="button"
                                    className="product-row-btn product-row-btn--delete"
                                    data-tooltip="Delete"
                                    onClick={(e) => handleRequestDeleteUnit(e, u)}
                                    aria-label="Delete"
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </>
                              ) : (
                                <span className="product-row-actions-locked" title="Void catalog item — unit edits disabled">
                                  —
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                  )}
                </>
              ))}
            </div>

            {partsSource && (
              <PartsInventoryTable
                title={partsSource === 'tim-e' ? 'TIM-E Parts Inventory' : 'BIM-E Parts Inventory'}
                rows={partsSource === 'tim-e' ? timEParts : bimEParts}
                onSaveRow={handleSavePartsRow}
                onSetStatus={handleSetPartsRowStatus}
                highlightPart={highlightPart}
                readOnly={partsSource === 'tim-e' ? !loaded.timEParts : !loaded.bimEParts}
              />
            )}

          </div>
        </main>
      </div>

      <Modal
        isOpen={!!unitToDelete}
        onClose={() => setUnitToDelete(null)}
        title="Delete inventory unit"
        narrow
      >
        <div className="modal-body">
          <p style={{ margin: '0 0 0.5rem', color: '#374151', fontSize: '0.9375rem' }}>
            {unitToDelete && product?.type === 'Stock Item' ? (
              <>
                Delete this stock row for <strong>{unitToDelete.manufacturer || '—'}</strong> at{' '}
                <strong>{unitToDelete.location || '—'}</strong>
                {unitToDelete.stockQuantity != null && unitToDelete.stockQuantity > 0
                  ? ` (count ${unitToDelete.stockQuantity})`
                  : ''}
                ?
              </>
            ) : (
              <>
                Are you sure you want to delete this inventory unit
                {unitToDelete?.serialNumber ? (
                  <>
                    {' '}
                    (serial <strong>{unitToDelete.serialNumber}</strong>)?
                  </>
                ) : (
                  '?'
                )}
              </>
            )}
          </p>
          <p style={{ margin: '0', color: '#8A8F93', fontSize: '0.875rem' }}>
            This action cannot be undone.
          </p>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={() => setUnitToDelete(null)}>
            Cancel
          </button>
          <button type="button" className="client-delete-confirm-btn" onClick={handleConfirmDeleteUnit}>
            Delete
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={voidProductOpen}
        onClose={() => setVoidProductOpen(false)}
        title="Void product"
        narrow
      >
        <div className="modal-body">
          <p style={{ margin: '0 0 0.5rem', color: '#374151', fontSize: '0.9375rem' }}>
            Void <strong>{product?.name}</strong>? It will be marked Void in the catalog (hidden from add-inventory;
            unit rows stay, but you cannot edit them until the product is Active again).
          </p>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={() => setVoidProductOpen(false)}>
            Cancel
          </button>
          <button type="button" className="client-delete-confirm-btn" onClick={handleConfirmVoidProduct}>
            Void product
          </button>
        </div>
      </Modal>

      {editProductOpen && (
        <div className="modal-overlay" onClick={() => setEditProductOpen(false)}>
          <div className="modal-content modal-content--narrow" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit product details</h3>
              <button type="button" className="modal-close-button" onClick={() => setEditProductOpen(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSaveProduct}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Catalog status</label>
                  <select
                    className="form-input"
                    value={editCatalogStatus}
                    onChange={(e) => setEditCatalogStatus(e.target.value as ProductCatalogStatus)}
                  >
                    <option value="Active">Active</option>
                    <option value="Void">Void</option>
                  </select>
                  {lockProductDetailFields && (
                    <span className="form-hint">
                      While status is Void, other fields are locked. Set status to Active to reactivate and edit details.
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Product Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editProductName}
                    onChange={(e) => setEditProductName(e.target.value)}
                    required={!lockProductDetailFields}
                    disabled={lockProductDetailFields}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select
                    className="form-input"
                    value={editProductType}
                    onChange={(e) => setEditProductType(e.target.value as ProductType)}
                    disabled={lockProductDetailFields}
                  >
                    <option value="Robot">Robot</option>
                    <option value="Accessory">Accessories</option>
                    <option value="Stock Item">Stock Item</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">SKU</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editProductSku}
                    onChange={(e) => setEditProductSku(e.target.value)}
                    placeholder="e.g. TIM-E-001"
                    disabled={lockProductDetailFields}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Replacement cost (per item)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editProductReplacementCost}
                    onChange={(e) => setEditProductReplacementCost(e.target.value)}
                    placeholder="e.g. $1,200"
                    disabled={lockProductDetailFields}
                  />
                  <span className="form-hint">
                    Free-form value (often a dollar amount). Used on the monthly TIM-E inventory report for column G
                    when the parts row has no replacement cost but a catalog product name matches this line.
                  </span>
                </div>
                <div className="form-group">
                  <label className="form-label">Low stock buffer</label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="form-input"
                    value={editProductLowStockBuffer}
                    onChange={(e) => setEditProductLowStockBuffer(e.target.value)}
                    placeholder="Default: 25"
                    aria-label="Low stock buffer — show on dashboard when below this number"
                    disabled={lockProductDetailFields}
                  />
                  <span className="form-hint">
                    When total available quantity falls below this number, the item appears on the dashboard low-stock
                    card (highlighted yellow). Leave blank to hide from the low-stock card. For stock items,
                    &quot;available&quot; is the sum of counts across placement rows.
                  </span>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-button" onClick={() => setEditProductOpen(false)}>Cancel</button>
                <button type="submit" className="save-button">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {addUnitOpen && (
        <div className="modal-overlay">
          <div className="modal-content add-inventory-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingUnit ? `Edit inventory — ${displayName}` : `Add inventory — ${displayName}`}</h3>
              <button type="button" className="modal-close-button" onClick={resetUnitFormAndClose} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSaveUnit} className="inventory-edit-form">
              <div className="modal-body">
                {isStockProduct ? (
                  <div className="inventory-modal-card">
                    <h4 className="inventory-modal-card-title">Stock</h4>
                    <p className="form-hint" style={{ marginTop: 0 }}>
                      Same manufacturer, location, and Deployed setting as an existing row adds to that
                      row&apos;s count when you use Add. Manufacturer and location are optional; leave blank
                      to merge into the unnamed placement row.
                    </p>
                    <div className="inventory-modal-card-fields">
                      <div className="form-group">
                        <label className="form-label">Manufacturer</label>
                        <input
                          type="text"
                          className="form-input"
                          value={manufacturer}
                          onChange={(e) => setManufacturer(e.target.value)}
                          placeholder="Manufacturer (optional)"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Location</label>
                        <input
                          type="text"
                          className="form-input"
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                          placeholder="Warehouse / site / shelf (optional)"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Deployed</label>
                        <select
                          className="form-input"
                          value={unitDeployed ? 'yes' : 'no'}
                          onChange={(e) => setUnitDeployed(e.target.value === 'yes')}
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                        <p className="form-hint">
                          Yes if these items are out at a business. No keeps them counted as available
                          in an office or warehouse.
                        </p>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Assembled</label>
                        <select
                          className="form-input"
                          value={unitAssembled ? 'yes' : 'no'}
                          onChange={(e) => setUnitAssembled(e.target.value === 'yes')}
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Count</label>
                        <input
                          type="number"
                          min={1}
                          className="form-input"
                          value={stockQuantity}
                          onChange={(e) => setStockQuantity(e.target.value)}
                          placeholder="1"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                <div className="inventory-modal-card">
                  <h4 className="inventory-modal-card-title">Location &amp; status</h4>
                  <div className="inventory-modal-card-fields">
                    <div className="form-group">
                      <label className="form-label">Location</label>
                      <input
                        type="text"
                        className="form-input"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="City / location"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Business</label>
                      <input
                        type="text"
                        className="form-input"
                        value={business}
                        onChange={(e) => setBusiness(e.target.value)}
                        placeholder="Business"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Installation Date</label>
                      <input
                        type="date"
                        className="form-input"
                        value={installationDate}
                        onChange={(e) => setInstallationDate(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <select
                        className="form-input"
                        value={status}
                        onChange={(e) => setStatus(e.target.value as InventoryUnitStatus | '')}
                      >
                        <option value="">—</option>
                        <option value="Deployed">Deployed</option>
                        <option value="In Storage">In Storage</option>
                        <option value="Repair">Repair</option>
                        <option value="Out of Order">Out of Order</option>
                        <option value="Event">Event</option>
                        <option value="In-Route">In-Route</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Condition</label>
                      <select
                        className="form-input"
                        value={condition}
                        onChange={(e) => setCondition(e.target.value as InventoryUnitCondition | '')}
                      >
                        <option value="">—</option>
                        <option value="Working">Working</option>
                        <option value="Broken">Broken</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Assembled</label>
                      <select
                        className="form-input"
                        value={unitAssembled ? 'yes' : 'no'}
                        onChange={(e) => setUnitAssembled(e.target.value === 'yes')}
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>
                  </div>
                </div>
                {isFullInventoryProduct && (
                  <div className="inventory-modal-card">
                    <h4 className="inventory-modal-card-title">Equipment counts</h4>
                    <div className="inventory-modal-card-fields inventory-modal-card-grid">
                      <div className="form-group">
                        <label className="form-label">Trash bins</label>
                        <input
                          type="number"
                          min={0}
                          className="form-input"
                          value={trashBins}
                          onChange={(e) => setTrashBins(e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Linen bins</label>
                        <input
                          type="number"
                          min={0}
                          className="form-input"
                          value={linenBins}
                          onChange={(e) => setLinenBins(e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Rolling bases</label>
                        <input
                          type="number"
                          min={0}
                          className="form-input"
                          value={rollingBases}
                          onChange={(e) => setRollingBases(e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Static bases</label>
                        <input
                          type="number"
                          min={0}
                          className="form-input"
                          value={staticBases}
                          onChange={(e) => setStaticBases(e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div className="inventory-modal-card">
                  <h4 className="inventory-modal-card-title">Product details</h4>
                  <div className="inventory-modal-card-fields">
                    <div className="form-group">
                      <label className="form-label">Manufacturer</label>
                      <input
                        type="text"
                        className="form-input"
                        value={manufacturer}
                        onChange={(e) => setManufacturer(e.target.value)}
                        placeholder="Manufacturer"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Country of origin</label>
                      <input
                        type="text"
                        className="form-input"
                        value={countryOfOrigin}
                        onChange={(e) => setCountryOfOrigin(e.target.value)}
                        placeholder="e.g. United States"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Product type (asset tag category)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={productType}
                        onChange={(e) => setProductType(e.target.value)}
                        placeholder="e.g. Garg"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Serial number</label>
                      <input
                        type="text"
                        className="form-input"
                        value={serialNumber}
                        onChange={(e) => setSerialNumber(e.target.value)}
                        placeholder="Optional"
                      />
                    </div>
                    {editingUnit?.tempSerialNumber && (
                      <div className="form-group">
                        <label className="form-label">Temporary serial number</label>
                        <input type="text" className="form-input" value={editingUnit.tempSerialNumber} disabled />
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">Model</label>
                      <input
                        type="text"
                        className="form-input"
                        value={model}
                        onChange={(e) => setModel(e.target.value)}
                        placeholder="Model"
                      />
                    </div>
                  </div>
                </div>
                  </>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-button" onClick={resetUnitFormAndClose}>Cancel</button>
                <button type="submit" className="save-button">{editingUnit ? 'Save' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProductDetail;
